import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { clean, count, money, percent, share } from "../format";

export const Card = ({ title, subtitle, action, flush, children }) => (
  <section className="dash-card" aria-label={title}>
    {(title || action) && (
      <header className="dash-card-head">
        <div>
          {title && <h6 className="dash-card-title">{title}</h6>}
          {subtitle && <p className="dash-card-sub">{subtitle}</p>}
        </div>
        {action}
      </header>
    )}
    <div className={`dash-card-body${flush ? " dash-card-body--flush" : ""}`}>{children}</div>
  </section>
);

export const Delta = ({ change, invert = false }) => {
  if (change === null || change === undefined) {
    return (
      <span className="dash-delta dash-delta--flat" title="No comparable prior period">
        &mdash;
      </span>
    );
  }

  const rising = change > 0;
  const good = invert ? !rising : rising;
  const tone = change === 0 ? "flat" : good ? "up" : "down";
  const direction = change === 0 ? "right" : rising ? "up" : "down";
  const spoken = change === 0 ? "No change" : `${rising ? "Up" : "Down"} ${percent(Math.abs(change))}`;

  return (
    <span className={`dash-delta dash-delta--${tone}`} title={spoken}>
      <i className={`fa fa-arrow-${direction}`} aria-hidden="true" />
      <span className="visually-hidden">{spoken}</span>
      <span aria-hidden="true">{percent(Math.abs(change))}</span>
    </span>
  );
};

export const Empty = ({ children = "No data for this period." }) => <div className="dash-empty">{children}</div>;

export const Skeleton = ({ height = 16, width = "100%", className = "" }) => (
  <div className={`dash-skeleton ${className}`} style={{ height, width }} aria-hidden="true" />
);

export const Notice = ({ children, actionLabel, onAction }) => (
  <div className="dash-notice" role="alert">
    <i className="fa fa-exclamation-circle" aria-hidden="true" />
    <span>{children}</span>
    {onAction && (
      <button type="button" className="dash-btn" onClick={onAction}>
        {actionLabel}
      </button>
    )}
  </div>
);

/**
 * Wraps a chart in the three states every panel shares, and gives screen
 * readers a text summary in place of the SVG.
 */
export const ChartPanel = ({ loading, empty, emptyText, height, summary, children }) => {
  if (loading) return <Skeleton height={height} />;
  if (empty) return <Empty>{emptyText}</Empty>;
  return (
    <div role="img" aria-label={summary}>
      {children}
    </div>
  );
};

/* ----------------------------------------------------------------- tooltips */

export const SeriesTooltip = ({ active, payload, label, format = money }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="dash-tip">
      <div className="dash-tip-title">{label}</div>
      {payload.map((entry) => (
        <div className="dash-tip-row" key={entry.dataKey}>
          <span className="dash-tip-key" style={{ background: entry.color || entry.fill }} />
          <span className="dash-tip-name">{entry.name}</span>
          <span className="dash-tip-value">{format(entry.value, entry.dataKey)}</span>
        </div>
      ))}
    </div>
  );
};

const TipRow = ({ name, value }) => (
  <div className="dash-tip-row">
    <span className="dash-tip-name">{name}</span>
    <span className="dash-tip-value">{value}</span>
  </div>
);

export const CategoryTooltip = ({ active, payload, total }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload || {};

  return (
    <div className="dash-tip">
      <div className="dash-tip-title">{clean(row.name)}</div>
      <TipRow name="Value" value={money(row.value)} />
      {total > 0 && <TipRow name="Share" value={percent(share(row.value, total))} />}
      {row.orders !== undefined && <TipRow name="Invoices" value={count(row.orders)} />}
      {row.qty !== undefined && <TipRow name="Quantity" value={count(row.qty)} />}
    </div>
  );
};

/* ------------------------------------------------------------------ info tip */

const TIP_GAP = 8;
const TIP_MARGIN = 16;

/**
 * An (i) button that explains a figure. The tip renders in a portal because
 * dashboard cards clip their overflow; it opens on hover, keyboard focus or
 * tap, closes on Escape, scroll or resize, and flips above the icon when
 * there is no room below.
 */
export const InfoTip = ({ label, children }) => {
  const id = useId();
  const buttonRef = useRef(null);
  const tipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [style, setStyle] = useState({ visibility: "hidden" });

  const close = useCallback(() => {
    setOpen(false);
    setPinned(false);
  }, []);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !tipRef.current) return;
    const anchor = buttonRef.current.getBoundingClientRect();
    const tip = tipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const left = Math.min(Math.max(anchor.left + anchor.width / 2 - tip.width / 2, TIP_MARGIN), vw - tip.width - TIP_MARGIN);
    const below = anchor.bottom + TIP_GAP;
    const top = below + tip.height > vh - TIP_MARGIN && anchor.top - TIP_GAP - tip.height > TIP_MARGIN ? anchor.top - TIP_GAP - tip.height : below;

    setStyle({ left, top });
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStyle({ visibility: "hidden" });
      return undefined;
    }
    const onKey = (e) => e.key === "Escape" && close();
    const onPointer = (e) => {
      if (!buttonRef.current?.contains(e.target) && !tipRef.current?.contains(e.target)) close();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open, close]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="dash-info"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => !pinned && setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => !pinned && setOpen(false)}
        onClick={() => {
          // Tap/click pins the tip open (touch has no hover); a second click closes it.
          if (pinned) {
            close();
          } else {
            setPinned(true);
            setOpen(true);
          }
        }}
      >
        <i className="fa fa-info-circle" aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          /* .dash carries the colour tokens the tip is styled with. */
          <div className="dash dash-infotip-layer">
            <div ref={tipRef} id={id} role="tooltip" className="dash-infotip" style={style}>
              {children}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
